import React, { useState } from 'react';
import {
  Grid,
  Card,
  CardContent,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  TablePagination,
  Chip,
  makeStyles,
} from '@material-ui/core';
import {
  TrendingUp as TrendingUpIcon,
  TrendingDown as TrendingDownIcon,
} from '@material-ui/icons';
import { i18n } from '../../translate/i18n';

const useStyles = makeStyles(theme => ({
  card: {
    height: '100%',
    display: 'flex',
    flexDirection: 'column',
  },
  cardContent: {
    flexGrow: 1,
    padding: theme.spacing(2),
  },
  tableTitle: {
    fontSize: '1.1rem',
    fontWeight: 500,
    marginBottom: theme.spacing(2),
    color: theme.palette.text.primary,
  },
  tableContainer: {
    maxHeight: 400,
  },
  table: {
    minWidth: 650,
  },
  tableHead: {
    backgroundColor: theme.palette.grey[100],
  },
  tableHeadCell: {
    fontWeight: 600,
    color: theme.palette.text.primary,
  },
  positiveChange: {
    color: '#24B4A5',
  },
  negativeChange: {
    color: '#FF6F61',
  },
  neutralChange: {
    color: theme.palette.text.secondary,
  },
  chip: {
    margin: theme.spacing(0.5),
  },
}));

const MetricsTables = ({ metrics, isAdmin }) => {
  const classes = useStyles();
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);

  if (!metrics) return null;

  const { performanceMetrics } = metrics;

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = event => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const formatValue = (value, type) => {
    switch (type) {
      case 'currency':
        return new Intl.NumberFormat('pt-BR', {
          style: 'currency',
          currency: 'BRL',
        }).format(value);
      case 'percentage':
        return `${value.toFixed(1)}%`;
      case 'time':
        return `${Math.floor(value / 60)}h ${value % 60}min`;
      default:
        return value.toLocaleString('pt-BR');
    }
  };

  const getChangeIcon = change => {
    if (change > 0) {
      return <TrendingUpIcon className={classes.positiveChange} />;
    } else if (change < 0) {
      return <TrendingDownIcon className={classes.negativeChange} />;
    }
    return null;
  };

  const getChangeClass = change => {
    if (change > 0) return classes.positiveChange;
    if (change < 0) return classes.negativeChange;
    return classes.neutralChange;
  };

  return (
    <Grid container spacing={3}>
      {/* Performance por Usuário (apenas admin) */}
      {isAdmin && (
        <Grid item xs={12} md={6}>
          <Card className={classes.card}>
            <CardContent className={classes.cardContent}>
              <Typography className={classes.tableTitle}>
                {i18n.t('metrics.tables.userPerformance')}
              </Typography>
              <TableContainer
                component={Paper}
                className={classes.tableContainer}
              >
                <Table className={classes.table} stickyHeader>
                  <TableHead className={classes.tableHead}>
                    <TableRow>
                      <TableCell className={classes.tableHeadCell}>
                        {i18n.t('metrics.tables.user')}
                      </TableCell>
                      <TableCell
                        className={classes.tableHeadCell}
                        align='right'
                      >
                        {i18n.t('metrics.tables.tickets')}
                      </TableCell>
                      <TableCell
                        className={classes.tableHeadCell}
                        align='right'
                      >
                        {i18n.t('metrics.tables.conversions')}
                      </TableCell>
                      <TableCell
                        className={classes.tableHeadCell}
                        align='right'
                      >
                        {i18n.t('metrics.tables.conversionRate')}
                      </TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {performanceMetrics.ticketsPerUser
                      .slice(
                        page * rowsPerPage,
                        page * rowsPerPage + rowsPerPage
                      )
                      .map((user, index) => {
                        const conversionRate =
                          user.ticketCount > 0
                            ? (user.conversionCount / user.ticketCount) * 100
                            : 0;

                        return (
                          <TableRow key={user.userId} hover>
                            <TableCell component='th' scope='row'>
                              {user.userName}
                            </TableCell>
                            <TableCell align='right'>
                              {user.ticketCount.toLocaleString('pt-BR')}
                            </TableCell>
                            <TableCell align='right'>
                              {user.conversionCount.toLocaleString('pt-BR')}
                            </TableCell>
                            <TableCell align='right'>
                              <Chip
                                label={`${conversionRate.toFixed(1)}%`}
                                className={classes.chip}
                                color={
                                  conversionRate >= 20 ? 'primary' : 'default'
                                }
                                size='small'
                              />
                            </TableCell>
                          </TableRow>
                        );
                      })}
                  </TableBody>
                </Table>
              </TableContainer>
              <TablePagination
                rowsPerPageOptions={[5, 10, 25]}
                component='div'
                count={performanceMetrics.ticketsPerUser.length}
                rowsPerPage={rowsPerPage}
                page={page}
                onPageChange={handleChangePage}
                onRowsPerPageChange={handleChangeRowsPerPage}
                labelRowsPerPage={i18n.t('metrics.tables.rowsPerPage')}
              />
            </CardContent>
          </Card>
        </Grid>
      )}

      {/* Performance por Fila (apenas admin) */}
      {isAdmin && (
        <Grid item xs={12} md={6}>
          <Card className={classes.card}>
            <CardContent className={classes.cardContent}>
              <Typography className={classes.tableTitle}>
                {i18n.t('metrics.tables.queuePerformance')}
              </Typography>
              <TableContainer
                component={Paper}
                className={classes.tableContainer}
              >
                <Table className={classes.table} stickyHeader>
                  <TableHead className={classes.tableHead}>
                    <TableRow>
                      <TableCell className={classes.tableHeadCell}>
                        {i18n.t('metrics.tables.queue')}
                      </TableCell>
                      <TableCell
                        className={classes.tableHeadCell}
                        align='right'
                      >
                        {i18n.t('metrics.tables.tickets')}
                      </TableCell>
                      <TableCell
                        className={classes.tableHeadCell}
                        align='right'
                      >
                        {i18n.t('metrics.tables.conversions')}
                      </TableCell>
                      <TableCell
                        className={classes.tableHeadCell}
                        align='right'
                      >
                        {i18n.t('metrics.tables.conversionRate')}
                      </TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {performanceMetrics.queuePerformance
                      .slice(
                        page * rowsPerPage,
                        page * rowsPerPage + rowsPerPage
                      )
                      .map((queue, index) => {
                        const conversionRate =
                          queue.ticketCount > 0
                            ? (queue.conversionCount / queue.ticketCount) * 100
                            : 0;

                        return (
                          <TableRow key={queue.queueId} hover>
                            <TableCell component='th' scope='row'>
                              {queue.queueName}
                            </TableCell>
                            <TableCell align='right'>
                              {queue.ticketCount.toLocaleString('pt-BR')}
                            </TableCell>
                            <TableCell align='right'>
                              {queue.conversionCount.toLocaleString('pt-BR')}
                            </TableCell>
                            <TableCell align='right'>
                              <Chip
                                label={`${conversionRate.toFixed(1)}%`}
                                className={classes.chip}
                                color={
                                  conversionRate >= 20 ? 'primary' : 'default'
                                }
                                size='small'
                              />
                            </TableCell>
                          </TableRow>
                        );
                      })}
                  </TableBody>
                </Table>
              </TableContainer>
              <TablePagination
                rowsPerPageOptions={[5, 10, 25]}
                component='div'
                count={performanceMetrics.queuePerformance.length}
                rowsPerPage={rowsPerPage}
                page={page}
                onPageChange={handleChangePage}
                onRowsPerPageChange={handleChangeRowsPerPage}
                labelRowsPerPage={i18n.t('metrics.tables.rowsPerPage')}
              />
            </CardContent>
          </Card>
        </Grid>
      )}

      {/* Ranking de Conversões - Apenas para Admin */}
      {isAdmin && (
        <Grid item xs={12}>
          <Card className={classes.card}>
            <CardContent className={classes.cardContent}>
              <Typography className={classes.tableTitle}>
                {i18n.t('metrics.tables.conversionRanking')}
              </Typography>
              <TableContainer
                component={Paper}
                className={classes.tableContainer}
              >
                <Table className={classes.table} stickyHeader>
                  <TableHead className={classes.tableHead}>
                    <TableRow>
                      <TableCell className={classes.tableHeadCell}>
                        {i18n.t('metrics.tables.rank')}
                      </TableCell>
                      <TableCell className={classes.tableHeadCell}>
                        {i18n.t('metrics.tables.user')}
                      </TableCell>
                      <TableCell
                        className={classes.tableHeadCell}
                        align='right'
                      >
                        {i18n.t('metrics.tables.conversions')}
                      </TableCell>
                      <TableCell
                        className={classes.tableHeadCell}
                        align='right'
                      >
                        {i18n.t('metrics.tables.totalValue')}
                      </TableCell>
                      <TableCell
                        className={classes.tableHeadCell}
                        align='right'
                      >
                        {i18n.t('metrics.tables.averageValue')}
                      </TableCell>
                      {/* <TableCell
                        className={classes.tableHeadCell}
                        align='right'
                      >
                        {i18n.t('metrics.tables.change')}
                      </TableCell> */}
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {performanceMetrics.ticketsPerUser
                      .sort((a, b) => b.conversionCount - a.conversionCount)
                      .slice(0, 10)
                      .map((user, index) => (
                        <TableRow key={user.userId} hover>
                          <TableCell component='th' scope='row'>
                            <Chip
                              label={`#${index + 1}`}
                              color={index < 3 ? 'primary' : 'default'}
                              size='small'
                            />
                          </TableCell>
                          <TableCell>{user.userName}</TableCell>
                          <TableCell align='right'>
                            {user.conversionCount.toLocaleString('pt-BR')}
                          </TableCell>
                          <TableCell align='right'>
                            {formatValue(
                              user.conversionCount * user.ticketTotalValue,
                              'currency'
                            )}
                          </TableCell>
                          <TableCell align='right'>
                            {formatValue(
                              user.conversionCount > 0
                                ? +user.ticketTotalValue / +user.conversionCount
                                : 0,
                              'currency'
                            )}
                          </TableCell>
                          {/* <TableCell align='right'>
                            <div
                              style={{
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'flex-end',
                              }}
                            >
                              {getChangeIcon(5)}
                              <span className={getChangeClass(5)}>+5%</span>
                            </div>
                          </TableCell> */}
                        </TableRow>
                      ))}
                  </TableBody>
                </Table>
              </TableContainer>
            </CardContent>
          </Card>
        </Grid>
      )}
    </Grid>
  );
};

export default MetricsTables;
